import { useState } from "react";
import { FormProvider, Controller, useFormContext } from "react-hook-form";
import {
  FormField,
  SectionDivider,
  RadioGroup,
  inputCls,
  readOnlyCls,
} from "../../../components";
import { CountrySelect, SurveyGrid } from "../shared/formUtils";
import { useKycOrgForm } from "./hooks/useKycOrgForm";
import {
  ORG_BUSINESS_TYPE_OPTIONS,
  ORG_PURPOSE_OPTIONS,
  PORTFOLIO_SIZE_OPTIONS,
  ANNUAL_DEPOSITS_OPTIONS,
  ORG_SOURCE_OF_FUNDS_OPTIONS,
  ORG_SOURCE_OF_WEALTH_OPTIONS,
  YES_NO_DIRECT_OPTIONS,
  NO_YES_OTHER_OPTIONS,
  CRYPTO_OPTIONS,
  MIFID_INSTRUMENT_ROWS,
  KNOWLEDGE_COLUMNS,
  EXPERIENCE_COLUMNS,
  TRADING_FREQUENCY_COLUMNS,
  PERIOD_COLUMNS,
  NET_WEALTH_OPTIONS,
  CAPITAL_NEED_OPTIONS,
  DEBT_BURDEN_OPTIONS,
  INVESTMENT_HORIZON_OPTIONS,
  INVESTMENT_GOAL_OPTIONS,
  RISK_COMFORT_OPTIONS,
  INVESTMENT_EXPERIENCE_OPTIONS,
  ESG_PREF_OPTIONS,
  ESG_PREF_TYPE_OPTIONS,
  ORG_INVESTMENT_HORIZON_OPTIONS,
  ORG_RISK_COMFORT_OPTIONS,
  ORG_LIQUIDITY_OPTIONS,
  MARKET_DROP_RESPONSE_OPTIONS,
  ORG_INVESTMENT_GOAL_OPTIONS,
} from "./KycOrgForm.options";
import { flattenKycOrg } from "./KycOrgForm.rules";
import { BenOwnerData } from "./KycOrgForm.schema";
import { KycOrgFormProps } from "./KycOrgForm.types";
import { ContactAutocomplete } from "@/components/AutoCompletes/ContactAutoComplete";
import { Button } from "@/components/Button/Button";

function BenOwnerBlock({ index }: { index: number }) {
  const { register } = useFormContext();
  const key = `amlBeneficialOwner${index}`;
  return (
    <div className="rounded-md border border-border p-3 space-y-3 bg-muted/20">
      <p className="text-sm font-semibold text-muted-foreground">
        Rettighetshaver {index}
      </p>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Fødselsnummer">
          <input className={inputCls} {...register(`${key}.ssn`)} />
        </FormField>
        <FormField label="Navn">
          <input className={inputCls} {...register(`${key}.name`)} />
        </FormField>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <FormField label="Eierandel (%)">
          <input
            type="number"
            min={0}
            max={100}
            className={inputCls}
            {...register(`${key}.ownership`, { valueAsNumber: true })}
          />
        </FormField>
        <FormField label="Stemmerettigheter (%)">
          <input
            type="number"
            min={0}
            max={100}
            className={inputCls}
            {...register(`${key}.votingRights`, { valueAsNumber: true })}
          />
        </FormField>
      </div>
      <FormField label="PEP">
        <select className={inputCls} {...register(`${key}.pep`)}>
          <option value="">Velg...</option>
          <option value="Ja">Ja</option>
          <option value="Nei">Nei</option>
        </select>
      </FormField>
    </div>
  );
}

export function KycOrgForm({
  contact,
  profileData,
  mode = "combined",
  onDocumentCreated,
  onClose,
}: KycOrgFormProps) {
  const methods = useKycOrgForm(contact, profileData, mode);
  const { register, handleSubmit, watch } = methods;
  const businessActivity = watch("amlBusinessActivity");
  const purpose = watch("amlPurposeOfCustomerRelationship");
  const sourceOfFunds = watch("amlSourceOfFunds");
  const transfersAbroad = watch("amlTransfersAbroad");
  const mifidEsgPreference = watch("mifidEsgPreference");

  const [visibleCount, setVisibleCount] = useState(() => {
    const vals = methods.getValues();
    let count = 0;
    for (let i = 1; i <= 5; i++) {
      const o = (vals as unknown as Record<string, BenOwnerData>)[
        `amlBeneficialOwner${i}`
      ];
      if (o?.ssn || o?.name) count = i;
    }
    return Math.max(1, count);
  });

  const [hasEducation, setHasEducation] = useState(() => {
    const v = methods.getValues("mifidRelevantExperienceEducation");
    return v !== "" && v !== "Nei";
  });

  const onSubmit = handleSubmit(
    (data) => {
      onDocumentCreated?.(flattenKycOrg(data, mode));
      onClose?.();
    },
    (errs) => console.error("KycOrgForm validation errors:", errs)
  );

  return (
    <FormProvider {...methods}>
      <form onSubmit={onSubmit} className="space-y-4 p-4">
        {/* ── Kundedata ── */}
        <SectionDivider title="Kundedata" />

        <FormField label="Selskapsnavn">
          <input readOnly className={readOnlyCls} {...register("orgName")} />
        </FormField>

        <FormField label="Organisasjonsnummer">
          <input readOnly className={readOnlyCls} {...register("orgNumber")} />
        </FormField>

        <FormField label="Adresse">
          <input className={inputCls} {...register("address")} />
        </FormField>

        <div className="grid grid-cols-2 gap-4">
          <FormField label="Postnummer">
            <input className={inputCls} {...register("zip")} />
          </FormField>
          <FormField label="Poststed">
            <input className={inputCls} {...register("city")} />
          </FormField>
        </div>

        <FormField label="Land">
          <CountrySelect {...register("taxCountry")} />
        </FormField>

        <FormField label="Telefon">
          <input className={inputCls} {...register("phone")} />
        </FormField>

        <FormField label="E-postadresse">
          <input className={inputCls} {...register("email")} />
        </FormField>

        {/* ── Selskapets kontaktperson ── */}
        <SectionDivider title="Selskapets kontaktperson" />

        <FormField label="Hovedkontakt">
          <Controller
            name="mcId"
            control={methods.control}
            render={({ field }) => (
              <ContactAutocomplete
                contactId={field.value || undefined}
                label="Velg kontakt"
                disableFavorites
                onChange={(_, contact) => {
                  field.onChange(contact?.contactId ?? "");
                  methods.setValue("mcName", contact?.name ?? "");
                }}
              />
            )}
          />
        </FormField>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              {...register("amlContactPersonPowerOfAttorney")}
              className="accent-blue-600 w-4 h-4"
            />
            <span>Fullmakt avgitt</span>
          </label>
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <input
              type="checkbox"
              {...register("amlContactPersonPep")}
              className="accent-blue-600 w-4 h-4"
            />
            <span>Kontaktperson PEP</span>
          </label>
        </div>

        {/* ── Reelle rettighetshavere ── */}
        <SectionDivider title="Reelle rettighetshavere" />

        <div className="space-y-3">
          {Array.from({ length: visibleCount }, (_, i) => i + 1).map((i) => (
            <BenOwnerBlock key={i} index={i} />
          ))}
        </div>

        {visibleCount < 5 && (
          <button
            type="button"
            onClick={() => setVisibleCount((n) => n + 1)}
            className="text-sm text-primary hover:underline"
          >
            + Legg til rettighetshaver
          </button>
        )}

        {/* ── Kundekontroll ── */}
        <SectionDivider title="Kundekontroll" />

        <FormField label="Hva er foretakets virksomhet?">
          <RadioGroup
            options={ORG_BUSINESS_TYPE_OPTIONS}
            vertical
            {...register("amlBusinessActivity")}
          />
        </FormField>

        {businessActivity === "other" && (
          <FormField label="Spesifiser virksomhet:">
            <input
              className={inputCls}
              {...register("amlBusinessActivityCustom")}
            />
          </FormField>
        )}

        <FormField label="Hva er formålet med kundeforholdet?">
          <RadioGroup
            options={ORG_PURPOSE_OPTIONS}
            vertical
            {...register("amlPurposeOfCustomerRelationship")}
          />
        </FormField>

        {purpose === "other" && (
          <FormField label="Spesifiser formål:">
            <input
              className={inputCls}
              {...register("amlPurposeOfCustomerRelationshipCustom")}
            />
          </FormField>
        )}

        {/* ── Forventet aktivitet og transaksjonsprofil ── */}
        <SectionDivider title="Forventet aktivitet og transaksjonsprofil" />

        <FormField label="Hvor stor vil investert portefølje hos Capguard være i løpet av kundeforholdet?">
          <RadioGroup
            options={PORTFOLIO_SIZE_OPTIONS}
            vertical
            {...register("amlInvestedPortfolio")}
          />
        </FormField>

        <FormField label="Hvor mange overførsler vil normalt gjøres i løpet av et år?">
          <RadioGroup
            options={ANNUAL_DEPOSITS_OPTIONS}
            vertical
            {...register("amlAnnualDeposits")}
          />
        </FormField>

        <FormField label="Hvor vil overføringer normalt komme fra?">
          <RadioGroup
            options={ORG_SOURCE_OF_FUNDS_OPTIONS}
            vertical
            {...register("amlSourceOfFunds")}
          />
        </FormField>

        {sourceOfFunds === "other" && (
          <FormField label="Spesifiser kilde:">
            <input
              className={inputCls}
              {...register("amlSourceOfFundsCustom")}
            />
          </FormField>
        )}

        <FormField label="Håndterer selskapet kontanter som en del av virksomhetens drift?">
          <RadioGroup
            options={YES_NO_DIRECT_OPTIONS}
            {...register("amlManagedOnBehalfOfOthers")}
          />
        </FormField>

        <FormField label="Planlegges overføring til eller fra konto utenfor Norge?">
          <RadioGroup
            options={NO_YES_OTHER_OPTIONS}
            {...register("amlTransfersAbroad")}
          />
        </FormField>

        {transfersAbroad === "other" && (
          <FormField label="Spesifiser land/formål:">
            <input
              className={inputCls}
              {...register("amlTransfersAbroadCustom")}
            />
          </FormField>
        )}

        <FormField label="Formuens opprinnelse?">
          <RadioGroup
            options={ORG_SOURCE_OF_WEALTH_OPTIONS}
            vertical
            {...register("amlSourceOfWealth")}
          />
        </FormField>

        <FormField label="Vil noen av overføringene ha sin opprinnelse fra kryptovaluta?">
          <RadioGroup
            options={CRYPTO_OPTIONS}
            {...register("amlCryptoCurrency")}
          />
        </FormField>

        {/* ── MiFID: Kunnskap og erfaring ── */}
        <SectionDivider title="Kunnskap og erfaring" />

        <div className="space-y-4">
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Kunnskap om finansielle instrumenter:</p>
            <SurveyGrid
              rows={MIFID_INSTRUMENT_ROWS.map((r) => ({ label: r.label, field: r.knowledgeField }))}
              columns={KNOWLEDGE_COLUMNS}
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Erfaring med finansielle instrumenter:</p>
            <SurveyGrid
              rows={MIFID_INSTRUMENT_ROWS.map((r) => ({ label: r.label, field: r.experienceField }))}
              columns={EXPERIENCE_COLUMNS}
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Handelsfrekvens:</p>
            <SurveyGrid
              rows={MIFID_INSTRUMENT_ROWS.map((r) => ({ label: r.label, field: r.tradingFrequencyField }))}
              columns={TRADING_FREQUENCY_COLUMNS}
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Investeringsperiode:</p>
            <SurveyGrid
              rows={MIFID_INSTRUMENT_ROWS.map((r) => ({ label: r.label, field: r.periodField }))}
              columns={PERIOD_COLUMNS}
            />
          </div>
        </div>

        <FormField label="Har kontaktperson og/eller selskapets investeringsteam relevant erfaring/utdanning innen finans?">
          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="radio"
                checked={hasEducation}
                onChange={() => {
                  setHasEducation(true);
                  methods.setValue("mifidRelevantExperienceEducation", "");
                }}
                className="accent-blue-600 w-3.5 h-3.5"
              />
              <span>Ja</span>
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="radio"
                checked={!hasEducation}
                onChange={() => {
                  setHasEducation(false);
                  methods.setValue("mifidRelevantExperienceEducation", "Nei");
                }}
                className="accent-blue-600 w-3.5 h-3.5"
              />
              <span>Nei</span>
            </label>
          </div>
        </FormField>

        {hasEducation && (
          <FormField label="Spesifiser (for kontaktperson og/eller selskapets investeringsteam):">
            <input
              className={inputCls}
              {...register("mifidRelevantExperienceEducation")}
            />
          </FormField>
        )}

        {/* ── MiFID: Finansiell situasjon ── */}
        <SectionDivider title="Finansiell situasjon" />

        <FormField label="Total netto formue">
          <RadioGroup
            options={NET_WEALTH_OPTIONS}
            {...register("mifidTotalNetWealth")}
          />
        </FormField>

        <FormField label="Investerbare likvide midler">
          <RadioGroup
            options={NET_WEALTH_OPTIONS}
            {...register("mifidLiquidInvestableWealth")}
          />
        </FormField>

        <FormField label="Planlagt kapitalbehov/uttak (24 måneder)">
          <RadioGroup
            options={CAPITAL_NEED_OPTIONS}
            {...register("mifidLiquidityBuffer")}
          />
        </FormField>

        <FormField label="Gjeldsbelastning">
          <RadioGroup
            options={DEBT_BURDEN_OPTIONS}
            {...register("mifidDebtBurden")}
          />
        </FormField>

        {/* ── MiFID: Horisont og mål ── */}
        <SectionDivider title="Horisont og mål" />

        <FormField label="Når forventer selskapet tidligst å ha behov for å ta ut en vesentlig del av denne investeringen/porteføljen?">
          <RadioGroup
            options={ORG_INVESTMENT_HORIZON_OPTIONS}
            vertical
            {...register("mifidEarliestWithdrawal")}
          />
        </FormField>

        <FormField label="Hva er hovedmålet med investeringen?">
          <RadioGroup
            options={ORG_INVESTMENT_GOAL_OPTIONS}
            vertical
            {...register("mifidMainInvestmentPurpose")}
          />
        </FormField>

        {/* ── MiFID: Risikotoleranse og adferd ── */}
        <SectionDivider title="Risikotoleranse og adferd" />

        <FormField label="Risikotoleranse: Hvor komfortabel er selskapet med svingninger i porteføljens verdi?">
          <RadioGroup
            options={ORG_RISK_COMFORT_OPTIONS}
            vertical
            {...register("mifidRiskToleranceVolatility")}
          />
        </FormField>

        <FormField label="Risikotoleranse: Hvordan ville selskapet håndtere et markedsfall på 30 % i porteføljen?">
          <RadioGroup
            options={MARKET_DROP_RESPONSE_OPTIONS}
            {...register("mifidMarketDropResponse")}
          />
        </FormField>

        <FormField label="Likviditetsbehov: Hvor viktig er det for deg å kunne trekke ut hele eller store deler av investeringen uten vesentlig verditap på kort varsel?">
          <RadioGroup
            options={ORG_LIQUIDITY_OPTIONS}
            {...register("mifidLiquidityNeed")}
          />
        </FormField>

        <FormField label="Hvis din samlede investeringsportefølje faller med 20–30 %, hvilken praktisk konsekvens vil det ha for selskapet?">
          <RadioGroup
            options={INVESTMENT_EXPERIENCE_OPTIONS}
            vertical
            {...register("mifidValueDeclineImpact")}
          />
        </FormField>

        {/* ── MiFID: ESG-preferanse ── */}
        <SectionDivider title="ESG-preferanse" />

        <FormField label="Har selskapet en egen bærekraftspreferanse som skal hensyntas ved valg av finansielle instrumenter?">
          <RadioGroup
            options={ESG_PREF_OPTIONS}
            {...register("mifidEsgPreference")}
          />
        </FormField>

        {mifidEsgPreference === "Ja" && (
          <FormField label="Gjelder dette preferanse 1, 2, 3 eller 4?">
            <RadioGroup
              options={ESG_PREF_TYPE_OPTIONS}
              vertical
              {...register("mifidEsgPreferenceType")}
            />
          </FormField>
        )}

        <div className="flex justify-end pt-4">
          <Button type="submit">Lagre</Button>
        </div>
      </form>
    </FormProvider>
  );
}
